import React from 'react';
import '../../css/components/duels/DuelRating.css';

/**
 * Компонент отображения рейтинга игрока
 * @param {Object} props
 * @param {Object} props.rating - Данные рейтинга
 * @param {number} props.rating.rating - Текущий рейтинг
 * @param {number} props.rating.rank - Позиция в рейтинге
 * @param {number} props.rating.wins - Победы
 * @param {number} props.rating.losses - Поражения
 * @param {number} props.rating.draws - Ничьи
 * @param {number} props.rating.totalDuels - Всего дуэлей
 * @param {number} props.rating.winRate - Процент побед
 * @param {number} [props.rating.ratingChange] - Изменение рейтинга
 * @param {Array<string>} [props.rating.recentForm] - Последние результаты (W, L, D)
 * @param {string} [props.size] - Размер: sm, md, lg
 */
function DuelRating({ rating, size = 'md' }) {
    if (!rating) {
        return null;
    }

    const {
        rating: ratingValue,
        rank,
        wins = 0,
        losses = 0,
        draws = 0,
        totalDuels = 0,
        winRate = 0,
        ratingChange,
        recentForm = []
    } = rating;

    // Определение цвета рейтинга
    const getRatingColor = (value) => {
        if (value >= 1500) return 'duel-rating__value--legendary';
        if (value >= 1300) return 'duel-rating__value--master';
        if (value >= 1100) return 'duel-rating__value--diamond';
        if (value >= 900) return 'duel-rating__value--gold';
        if (value >= 700) return 'duel-rating__value--silver';
        return 'duel-rating__value--bronze';
    };

    // Форматирование изменения рейтинга
    const formatRatingChange = (change) => {
        if (change === undefined || change === null) return null;
        const sign = change >= 0 ? '+' : '';
        const className = change >= 0 ? 'duel-rating__change--positive' : 'duel-rating__change--negative';
        return (
            <span className={`duel-rating__change ${className}`}>
                {sign}{change}
            </span>
        );
    };

    // Отображение формы (последние игры)
    const renderForm = () => {
        if (recentForm.length === 0) return null;

        return (
            <div className="duel-rating__form">
                {recentForm.map((result, index) => {
                    const className = `duel-rating__form-item duel-rating__form-item--${result.toLowerCase()}`;
                    const label = result === 'W' ? 'Победа' : result === 'L' ? 'Поражение' : 'Ничья';
                    return (
                        <span
                            key={index}
                            className={className}
                            title={label}
                        >
                            {result}
                        </span>
                    );
                })}
            </div>
        );
    };

    return (
        <div className={`duel-rating duel-rating--${size}`}>
            <div className="duel-rating__header">
                <div className="duel-rating__main">
                    <span className={`duel-rating__value ${getRatingColor(ratingValue)}`}>
                        {ratingValue}
                    </span>
                    {formatRatingChange(ratingChange)}
                </div>
                <div className="duel-rating__rank">
                    #{rank}
                </div>
            </div>

            <div className="duel-rating__stats">
                <div className="duel-rating__stat">
                    <span className="duel-rating__stat-value">{wins}</span>
                    <span className="duel-rating__stat-label">Победы</span>
                </div>
                <div className="duel-rating__stat">
                    <span className="duel-rating__stat-value">{losses}</span>
                    <span className="duel-rating__stat-label">Поражения</span>
                </div>
                <div className="duel-rating__stat">
                    <span className="duel-rating__stat-value">{draws}</span>
                    <span className="duel-rating__stat-label">Ничьи</span>
                </div>
                <div className="duel-rating__stat">
                    <span className="duel-rating__stat-value">{totalDuels}</span>
                    <span className="duel-rating__stat-label">Всего</span>
                </div>
                <div className="duel-rating__stat">
                    <span className="duel-rating__stat-value">{winRate.toFixed(1)}%</span>
                    <span className="duel-rating__stat-label">Win Rate</span>
                </div>
            </div>

            {renderForm()}
        </div>
    );
}

export default DuelRating;
